import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, Menu, X } from 'lucide-react';
import { AnimatePresence } from 'framer-motion';
import ThemeToggle from './ThemeToggle';

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'About', href: '#about' },
    { name: 'Work', href: '#work' },
    { name: 'Leadership', href: '#leadership' },
    { name: 'Projects', href: '#projects' },
    { name: 'Contact', href: '#contact' },
  ];

  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsOpen(false);
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.55 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'px-3 pt-3 sm:px-6' : 'px-0 pt-0'
      }`}
    >
      <div className={`mx-auto max-w-7xl transition-all duration-300 ${scrolled ? 'glass-strong rounded-2xl border border-white/10 shadow-cosmic-glass' : ''}`}>
        <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="flex items-center space-x-3"
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-aurora-borealis shadow-aurora-sapphire">
              <span className="text-white font-bold text-sm">SM</span>
            </div>
            <div className="leading-tight">
              <span className="block font-display text-lg font-bold gradient-text">
                Shivansh Mane
              </span>
              <span className="hidden text-[10px] uppercase tracking-[0.28em] text-slate-400 sm:block">
                Software Engineer
              </span>
            </div>
          </motion.div>

          <div className="hidden md:flex items-center space-x-3">
            {navItems.map((item, index) => (
              <motion.button
                key={item.name}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -1 }}
                onClick={() => scrollToSection(item.href)}
                className="rounded-full px-4 py-2 text-sm font-medium text-slate-700 transition-all duration-300 hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus:text-cosmic-aurora-emerald dark:text-slate-300 dark:hover:bg-white/5 dark:hover:text-white"
              >
                {item.name}
              </motion.button>
            ))}

            <ThemeToggle />
            <a
              href="#contact"
              onClick={(event) => {
                event.preventDefault();
                scrollToSection('#contact');
              }}
              className="btn-secondary px-4 py-2 text-sm"
            >
              Let&apos;s build
              <ArrowUpRight size={16} />
            </a>
          </div>

          <div className="md:hidden flex items-center space-x-2">
            <ThemeToggle />
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsOpen(!isOpen)}
              className="rounded-full border border-slate-200 bg-slate-50 p-2 text-slate-900 dark:border-white/10 dark:bg-white/5 dark:text-slate-100"
              aria-label="Toggle navigation menu"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </motion.button>
          </div>
        </div>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden border-t border-slate-200 bg-slate-50/95 px-2 pb-4 backdrop-blur-2xl dark:border-white/10 dark:bg-slate-950/75"
            >
              <div className="space-y-2 px-2 pt-4">
                {navItems.map((item, index) => (
                  <motion.button
                    key={item.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ x: 6 }}
                    onClick={() => scrollToSection(item.href)}
                    className="block w-full rounded-2xl px-4 py-3 text-left text-slate-700 transition-colors duration-300 hover:bg-slate-100 hover:text-cosmic-aurora-emerald focus:outline-none dark:text-slate-200 dark:hover:bg-white/5"
                  >
                    {item.name}
                  </motion.button>
                ))}
                <button
                  onClick={() => scrollToSection('#contact')}
                  className="btn-secondary mt-2 w-full"
                >
                  Start a conversation
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default Navbar;
